import React from 'react';
import moment from 'moment-business-days';

import _ from 'underscore';
import { getFrequentShippers } from 'actions/ReportingActionCreators';
import { Col, Row, ButtonToolbar, Button } from 'react-bootstrap';
import Container from 'components/v2/container';
import MARKETS from 'constants/Markets';
import { Trans, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import GrowthMetricTable from './GrowthMetricTable';
import DatePicker from '../../time/datepicker';
import { snakify } from '../../../utils/localization/utils';

moment.updateLocale('us', {
  holidays: [
    '12-25-2015',
    '01-01-2016',
    '05-30-2016',
    '07-04-2016',
    '09-05-2016',
    '11-24-2016',
    '12-26-2016',
  ],
  holidayFormat: 'MM-DD-YYYY',
});

function getDefaultFilters() {
  return {
    channel: {
      insideSales: false,
      enterpriseSales: false,
    },
    shipmentType: {
      drayage: false,
      shorthaul: false,
      longhaul: false,
      project: false,
      dayrate: false,
    },
    customerType: {
      frequentShipper: false,
    },
    businessType: {
      newStart: false,
      existing: false,
    },
  };
}

const shingleify = (term) => {
  return snakify(
    term.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()),
  );
};

class GrowthReport extends React.Component {
  constructor() {
    super();
    this.state = this.getInitialState();
  }

  getInitialState() {
    const dateMoment = moment();
    const markets = ['*'].concat(MARKETS.map((market) => market.value));
    return {
      markets,
      daysInPeriod: dateMoment.businessDaysIntoMonth(),
      periodName: dateMoment.format('MMMM'),
      comparisonPeriodName: dateMoment
        .clone()
        .subtract(1, 'month')
        .format('MMMM'),
      selectedDate: dateMoment.toDate(),
      filters: getDefaultFilters(),
    };
  }

  componentDidMount() {
    getFrequentShippers(this.state.selectedDate);
  }

  onDateChanged(date) {
    this.setState((prevState) => {
      return {
        ...prevState,
        periodName: moment(date).format('MMMM'),
        comparisonPeriodName: moment(date).subtract(1, 'month').format('MMMM'),
        selectedDate: date,
        daysInPeriod: moment(date).businessDaysIntoMonth(),
      };
    });
  }

  filter(name, setting) {
    return () => {
      this.setState((prevState) => {
        const filterSettings = getDefaultFilters()[name];
        let filters = prevState.filters;

        if (setting) filterSettings[setting] = true;

        filters[name] = filterSettings;

        return {
          ...prevState,
          filters,
        };
      });
    };
  }

  buildFilterButtons(name) {
    const filters = this.state.filters[name];
    const { t } = this.props;
    let buttons = _(filters).map((value, setting) => {
      return (
        <Button
          key={setting}
          bsStyle={value ? 'success' : 'default'}
          bsSize="small"
          onClick={this.filter(name, setting)}
          active={value}
        >
          {titleize(t(shingleify(setting)))}
        </Button>
      );
    });

    const noFilter = !_.compact(_.values(filters)).length;

    const all = (
      <Button
        key="all"
        bsStyle={noFilter ? 'success' : 'default'}
        bsSize="small"
        onClick={this.filter(name)}
        active={noFilter}
      >
        {titleize(t('ALL'))}
      </Button>
    );

    buttons.push(all);

    return buttons;
  }

  getSalesChannel() {
    const { t } = this.props;
    if (this.state.filters.channel.enterpriseSales)
      return t('ENTERPRISE_SALES');
    if (this.state.filters.channel.insideSales) return t('INSIDE_SALES');
    return t('ALL_SALES');
  }

  getShipmentType() {
    const { t } = this.props;
    if (this.state.filters.shipmentType.drayage) return t('DRAYAGE_SHIPMENTS');
    if (this.state.filters.shipmentType.shorthaul)
      return t('SHORTHAUL_SHIPMENTS');
    if (this.state.filters.shipmentType.project) return t('PROJECT_SHIPMENTS');
    if (this.state.filters.shipmentType.longhaul)
      return t('LONGHAUL_SHIPMENTS');
    if (this.state.filters.shipmentType.dayrate) return t('DAY_RATE_SHIPMENTS');
    return t('ALL_SHIPMENTS');
  }

  getCustomerType() {
    const { t } = this.props;
    if (this.state.filters.customerType.frequentShipper)
      return t('FREQUENT_SHIPPERS');
    return t('ALL_CUSTOMERS');
  }

  render() {
    const state = this.state;
    const { periodName, daysInPeriod } = this.state;
    const { t } = this.props;
    const growthMetricsTables = state.markets.map((market, key) => {
      return (
        <Col sm={6} key={key}>
          <GrowthMetricTable
            date={state.selectedDate}
            periodName={state.periodName}
            comparisonPeriodName={state.comparisonPeriodName}
            filters={state.filters}
            market={market}
          />
        </Col>
      );
    });

    const salesChannelButtons = this.buildFilterButtons('channel');
    const shipmentTypeButtons = this.buildFilterButtons('shipmentType');
    const customerTypeButtons = this.buildFilterButtons('customerType');
    const businessTypeButtons = this.buildFilterButtons('businessType');

    return (
      <Container>
        <div className="mt-10">
          <h2>{t('GROWTH_REPORT')}</h2>
          <Row>
            <Col sm={3}>
              <DatePicker
                label={t('SELECT_A_DAY')}
                initialDate={state.selectedDate}
                maxDate={new Date()}
                onChange={this.onDateChanged.bind(this)}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3}>
              <label>{t('SALES_CHANNEL')}</label>
              <ButtonToolbar>{salesChannelButtons}</ButtonToolbar>
            </Col>
            <Col sm={3}>
              <label>{t('SHIPMENT_TYPE')}</label>
              <ButtonToolbar>{shipmentTypeButtons}</ButtonToolbar>
            </Col>
            <Col sm={3}>
              <label>{t('CUSTOMER_TYPE')}</label>
              <ButtonToolbar>{customerTypeButtons}</ButtonToolbar>
            </Col>
            <Col sm={3}>
              <label>{t('NEW_BUSINESS')}</label>
              <ButtonToolbar>{businessTypeButtons}</ButtonToolbar>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <hr />
              <p className="lead">
                {this.getSalesChannel()}
                {' ,'}
                {this.getShipmentType()}
                {', '}
                {this.getCustomerType()}
                {' - '}
                <Trans i18nKey="PERIOD_BUSINESS_DAY_PERIOD_NAME">
                  <strong>{{ daysInPeriod }}</strong> business days into{' '}
                  <strong>{{ periodName }}</strong>
                </Trans>
              </p>
            </Col>
          </Row>
          <Row>{growthMetricsTables}</Row>
        </div>
      </Container>
    );
  }
}

export default withTranslation()(GrowthReport);
